import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useRef, useState, useEffect } from "react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { UpdateOrganizationData } from "../../service/User";
import { Trash2 } from "lucide-react";
import translations from "../common/Translations";
import { uploadFileToS3 } from "../../service/S3Bucket";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";

export default function UpdateOrganization({
  updateopen,
  setUpdateOpen,
  editOrganizationname,
  setEditOrganizationName,
  editOrganizationimg,
  setEditOrganizationImg,
  editReportEmails = [],
  setEditReportEmails,
  orgnizationId,
  fetchorgnizationlist,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(editOrganizationname);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(editOrganizationimg);
  const cancelButtonRef = useRef(null);
  const { theme, langMode } = useContext(AppContext);
  const fileInputRef = useRef(null);

  const reportEmails = editReportEmails?.length ? editReportEmails : [""];
  const addReportEmail = () => setEditReportEmails?.([...reportEmails, ""]);
  const removeReportEmail = (index) => {
    if (reportEmails.length <= 1) return;
    setEditReportEmails?.(reportEmails.filter((_, i) => i !== index));
  };
  const setReportEmailAt = (index, value) => {
    const next = [...reportEmails];
    next[index] = value;
    setEditReportEmails?.(next);
  };

  useEffect(() => {
    if (!updateopen) {
      setLogo(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [updateopen]);

  useEffect(() => {
    if (!updateopen) return;
    setSelectedOrgName(editOrganizationname);
    if (!logo) {
      setLogoPreview(editOrganizationimg || null);
    }
  }, [updateopen, editOrganizationname, editOrganizationimg, logo]);

  const handleOrgNameChange = (event) => {
    setSelectedOrgName(event.target.value);
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) {
      setLogo(null);
      setLogoPreview(editOrganizationimg || null);
      return;
    }
    setLogo(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateOrganization = async () => {
    setIsLoading(true);
    try {
      let newLogoUrl = editOrganizationimg;
      if (logo) {
        newLogoUrl = await uploadFileToS3(logo);
      }
      const emails = (reportEmails || []).map((e) => (typeof e === "string" ? e.trim() : "")).filter(Boolean);
      const data = await UpdateOrganizationData(
        selectedOrgName,
        newLogoUrl,
        orgnizationId,
        emails
      );
      if (data.header.code === 600) {
        toast.success(translations[langMode].orgupdated);
        setEditOrganizationName(selectedOrgName);
        setEditOrganizationImg(newLogoUrl);
        setSelectedOrgName("");
        setLogo(null);
        setUpdateOpen(false);
        fetchorgnizationlist();
      } else {
        toast.error(data.body.value);
      }
    } catch (error) {
      toast.error(error?.response?.data?.body?.value || "Error updating organization");
    } finally {
      setIsLoading(false);
    }
  };


  const handleClick = () => {
    fileInputRef.current.click(); // This opens the file upload modal
  };

  return (
    <Transition.Root show={updateopen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={setUpdateOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </Transition.Child>
        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative flex max-h-[calc(100dvh-1rem)] w-full transform flex-col overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:rounded-2xl ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
              >
                <div className={`bg-white ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {translations[langMode].updateOrg}
                    </h3>
                    <ModalCloseButton
                      onClick={() => setUpdateOpen(false)}
                      ariaLabel="Close modal"
                    />
                  </div>
                  <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-4 sm:px-5 sm:py-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                        {translations[langMode].orgname}
                      </label>
                      <input
                        type="text"
                        value={selectedOrgName}
                        onChange={handleOrgNameChange}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">
                        {translations[langMode].reportEmailsOptional}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {translations[langMode].reportEmailsDescription}
                      </p>
                      <div className="space-y-2">
                        {reportEmails.map((email, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <input
                              type="email"
                              value={typeof email === "string" ? email : ""}
                              onChange={(e) => setReportEmailAt(index, e.target.value)}
                              placeholder="email@example.com"
                              className={`flex-1 rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeReportEmail(index)}
                              disabled={reportEmails.length <= 1}
                              className="p-2 rounded text-[#FF0000] hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed dark:hover:bg-gray-600"
                              title={translations[langMode].removeEmail}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addReportEmail}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        >
                          + {translations[langMode].addAnotherEmail}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">
                        {translations[langMode].OrganizationLogo}
                      </label>
                      {!logoPreview ? (
                        <div
                          onClick={handleClick}
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-blue-500 rounded-lg cursor-pointer hover:bg-blue-50 transition"
                        >
                          <img
                            src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344927/uploadFile_qyzcdu.png"
                            className="w-8 h-8 mb-2"
                            alt="upload"
                          />
                          <p className="text-sm text-gray-700 font-medium">{translations[langMode].uploadOrganizationLogo}</p>
                          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleLogoChange} className="hidden" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <img
                            src={logoPreview}
                            alt="Logo Preview"
                            className="w-24 h-24 object-cover rounded-full border-2 border-gray-300"
                          />
                          <button data-button-intent="primary"
                            type="button"
                            onClick={handleClick}
                            className="px-3 py-1 text-sm font-medium text-blue-600 border border-blue-500 rounded hover:bg-blue-50"
                          >
                            {translations[langMode].uploadOrganizationLogo}
                          </button>
                          <input type="file" accept="image/*" ref={fileInputRef} onChange={handleLogoChange} className="hidden" />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 sticky bottom-0 bg-white dark:bg-gray-800 flex w-full justify-end items-center gap-2 sm:gap-3 border-t px-4 sm:px-5 py-3 sm:py-4">
                    <CommonButton colorIntent="danger"
                      onClick={() => setUpdateOpen(false)}
                      variant="danger"
                      wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                      className="w-full !h-10 !rounded-lg justify-center"
                    >
                      {translations[langMode].cancel}
                    </CommonButton>
                    <CommonButton colorIntent="success"
                      onClick={handleUpdateOrganization}
                      variant="primary"
                      wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                      className="w-full !h-10 !rounded-lg justify-center"
                    >
                      {translations[langMode].Update}
                    </CommonButton>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
